/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  ApolloCache,
  DocumentNode,
  NormalizedCacheObject,
} from '@apollo/client';
import { ChecklistCheckItemDueDocument } from './ChecklistCheckItemDueQuery.generated';
import { ChecklistCheckItemAllDocument } from './ChecklistCheckItemAllQuery.generated';
import { Maybe, Checklist, CheckItem } from '../../../generated';
import { firstLetterToUpper } from '../../stringOperations';

type ChecklistFilter = 'all' | 'due';

type SupportedParent = Checklist;

export type GeneralizedCardChecklistsQuery<T extends SupportedParent> = {
  __typename: 'Query';
} & {
  [K in Lowercase<T['__typename']>]?: Maybe<
    Pick<T, 'id' | '__typename'> & {
      checkItems: Array<{ __typename: 'CheckItem' } & Pick<Checklist, 'id'>>;
    }
  >;
};

const QUERY_MAP: Record<
  Lowercase<SupportedParent['__typename']>,
  Record<ChecklistFilter, DocumentNode>
> = {
  checklist: {
    all: ChecklistCheckItemAllDocument,
    due: ChecklistCheckItemDueDocument,
  },
};

function readChecklistCheckItemsFromCache<Parent extends SupportedParent>({
  cache,
  query,
  parentType,
  parentId,
}: {
  cache: ApolloCache<NormalizedCacheObject>;
  query: DocumentNode;
  parentType: Lowercase<Parent['__typename']>;
  parentId: string;
}) {
  return cache.readQuery<GeneralizedCardChecklistsQuery<Parent>>({
    query,
    variables: {
      parentId,
    },
  })?.[parentType]?.checkItems;
}

function writeChecklistCheckItemsToCache<Parent extends SupportedParent>({
  cache,
  query,
  parentType,
  parentId,
  checkItems,
}: {
  cache: ApolloCache<NormalizedCacheObject>;
  query: DocumentNode;
  parentType: Lowercase<Parent['__typename']>;
  parentId: string;
  checkItems: Partial<CheckItem>[];
}) {
  return cache.writeQuery({
    query,
    data: {
      __typename: 'Query',
      [`${parentType}`]: {
        id: parentId,
        checkItems,
        __typename: firstLetterToUpper(parentType),
      },
    },
    variables: {
      parentId,
    },
  });
}

export function assignCheckItemToFilters<Parent extends SupportedParent>({
  addTo,
  removeFrom,
  cache,
  parentType,
  parentIds,
  checkItemId,
}: {
  addTo?: ChecklistFilter[];
  removeFrom?: ChecklistFilter[];
  cache: ApolloCache<NormalizedCacheObject>;
  parentType: Lowercase<Parent['__typename']>;
  parentIds: string[];
  checkItemId: string;
}) {
  parentIds.forEach((parentId) => {
    if (addTo && addTo.length > 0) {
      addTo.forEach((filter) => {
        addCheckItemToChecklist({
          filter,
          cache,
          parentType,
          parentId,
          checkItemId,
        });
      });
    }
    if (removeFrom && removeFrom.length > 0) {
      removeFrom.forEach((filter) => {
        removeCheckItemFromChecklist({
          filter,
          cache,
          parentType,
          parentId,
          checkItemId,
        });
      });
    }
  });
}

function removeCheckItemFromChecklist<Parent extends SupportedParent>({
  filter,
  cache,
  parentType,
  parentId,
  checkItemId,
}: {
  filter: ChecklistFilter;
  cache: ApolloCache<NormalizedCacheObject>;
  parentType: Lowercase<Parent['__typename']>;
  parentId: string;
  checkItemId: string;
}) {
  const query = QUERY_MAP[parentType][filter];
  const params = { cache, query, parentType, parentId };
  const existingCheckItemsMatchingFilter = readChecklistCheckItemsFromCache(
    params,
  );

  const newCardsMatchingFilter = existingCheckItemsMatchingFilter?.filter(
    (checkItem) => checkItem.id !== checkItemId,
  );

  writeChecklistCheckItemsToCache({
    ...params,
    checkItems: [...(newCardsMatchingFilter ?? [])],
  });
}

function addCheckItemToChecklist<Parent extends SupportedParent>({
  filter,
  cache,
  parentType,
  parentId,
  checkItemId,
}: {
  filter: ChecklistFilter;
  cache: ApolloCache<NormalizedCacheObject>;
  parentType: Lowercase<Parent['__typename']>;
  parentId: string;
  checkItemId: string;
}) {
  const query = QUERY_MAP[parentType][filter];
  const params = { cache, query, parentType, parentId };
  const existingCheckItemsMatchingFilter = readChecklistCheckItemsFromCache(
    params,
  );

  const newChecklistsMatchingFilter = existingCheckItemsMatchingFilter?.filter(
    (checkItem) => checkItem.id !== checkItemId,
  );
  writeChecklistCheckItemsToCache({
    ...params,
    checkItems: [
      ...(newChecklistsMatchingFilter ?? []),
      {
        id: checkItemId,
        __typename: 'CheckItem',
      },
    ],
  });
}
